import { useRef } from 'react';

import ReactToPrint from 'react-to-print';
import { FcPrint } from 'react-icons/fc';
import { QRCodeCanvas } from 'qrcode.react';
import styled from 'styled-components';

import { ContainerType } from 'data/__generated__';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';

export type BoxToPrint = {
    bundleNumber: number;
    hasOnsiteSetup: boolean;
    isFreefood: boolean;
    roundColor: string;
    roundName: string;
    label: string;
    code: string;
    deliveryCode: string;
    containerType: ContainerType;
    columnName: string | null;
};

function getFlowEmoji(flow: string): string | null {
    switch (flow) {
        case 'bakery':
            return '🥐';
        case 'dry':
            return '🥜';
        case 'fresh':
            return '❄️';
        case 'fruit':
            return '🍌';
        case 'caterer':
            return '🍽️';
        case 'kumo':
            return '🍣';
        default:
            return null;
    }
}

export const PrintBoxTags = ({ boxesToPrint }: { boxesToPrint: BoxToPrint[] }) => {
    const componentRef = useRef(null);

    // Group boxes into pages of 6 (3 columns x 2 rows)
    const pages: BoxToPrint[][] = [];
    for (let i = 0; i < boxesToPrint.length; i += 6) {
        pages.push(boxesToPrint.slice(i, i + 6));
    }

    return (
        <Container>
            <ReactToPrint
                trigger={() => {
                    // NOTE: could just as easily return <SomeComponent />. Do NOT pass an `onClick` prop
                    // to the root node of the returned component as it will be overwritten.
                    return (
                        <span>
                            <TotemPrimaryButton disabled={!boxesToPrint.length}>
                                <PrintButtonContent>
                                    <FcPrint size={20} />
                                    <PrintLabel>Imprimer les étiquettes</PrintLabel>
                                </PrintButtonContent>
                            </TotemPrimaryButton>
                        </span>
                    );
                }}
                content={() => componentRef.current}
            />
            <HiddenComponent>
                <Printable ref={componentRef}>
                    {pages.map((pageBoxes, pageIndex) => (
                        <Page key={pageIndex}>
                            {pageBoxes.map((box, index) => {
                                const {
                                    bundleNumber,
                                    hasOnsiteSetup,
                                    isFreefood,
                                    roundColor,
                                    roundName,
                                    label,
                                    deliveryCode,
                                    columnName,
                                } = box;

                                const [tagLabel, , deliveryDate, boxType, boxNumber, flow] = label.split(' • ');

                                const tagSubLabel = [deliveryDate, boxType, boxNumber].join(' • ');

                                return (
                                    <BoxTagContainer key={index} $hasBackground={isFreefood}>
                                        <TagHeader>
                                            <HeaderLeft>
                                                <span>{bundleNumber}</span>
                                            </HeaderLeft>
                                            <HeaderCenter>
                                                <RoundHeaderLabel $roundColor={roundColor}>
                                                    {roundName}
                                                </RoundHeaderLabel>
                                            </HeaderCenter>
                                            <HeaderRight>
                                                {hasOnsiteSetup ? null : <span>🛍️</span>}
                                                <span>{getFlowEmoji(flow)}</span>
                                            </HeaderRight>
                                        </TagHeader>
                                        <TagContent>
                                            <Label $isTextLong={tagLabel.length > 23} $hasOnsiteSetup={hasOnsiteSetup}>
                                                {tagLabel}
                                            </Label>
                                            {columnName ? <ColumnName>{columnName}</ColumnName> : null}
                                            <SubLabel>{tagSubLabel}</SubLabel>
                                        </TagContent>
                                        <TagFooter>
                                            <SideRoundLabel $roundColor={roundColor}>{roundName}</SideRoundLabel>
                                            <QRCodeWrapper>
                                                <QRCodeCanvas value={deliveryCode} size={160} level="H" />
                                            </QRCodeWrapper>
                                            <SideRoundLabel $roundColor={roundColor}>{roundName}</SideRoundLabel>
                                        </TagFooter>
                                        <QRCodeValue>{deliveryCode}</QRCodeValue>
                                    </BoxTagContainer>
                                );
                            })}
                        </Page>
                    ))}
                </Printable>
            </HiddenComponent>
        </Container>
    );
};

const Container = styled.div`
    color: black;
`;

const PrintButtonContent = styled.div`
    display: flex;
    align-items: center;
`;

const PrintLabel = styled.span`
    margin-left: 5px;
`;

const HiddenComponent = styled.div`
    display: none;
`;

const Printable = styled.div`
    @page {
        size: landscape;
        margin: 10mm 12mm;
    }
`;

const Page = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: 1fr 1fr;
    width: 100%;
    height: 100vh;
    transform: scale(0.95);
    transform-origin: top left;
    break-after: page;
`;

const BoxTagContainer = styled.div<{ $hasBackground: boolean }>`
    display: flex;
    flex-direction: column;
    padding: 8px 10px;
    border: 1px solid black;
    break-inside: avoid;
    overflow: hidden;
    box-sizing: border-box;
    background-color: ${({ $hasBackground }) => ($hasBackground ? 'lightblue' : 'transparent')};
`;

const TagHeader = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    color: black;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
`;

const HeaderCenter = styled.div`
    display: flex;
    justify-content: center;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    flex: 1;
`;

const QRCodeWrapper = styled.div`
    display: inline-block;
`;

const RoundHeaderLabel = styled.span<{ $roundColor: string }>`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 4px;
    font-size: 24px;
    font-weight: 800;
    color: white;
    background-color: ${({ $roundColor }) => $roundColor};
    padding: 2px 8px;
    border-radius: 4px;
    align-self: center;
`;

const TagContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1;
    margin-top: 5px;
`;

const Label = styled.span<{ $hasOnsiteSetup: boolean; $isTextLong: boolean }>`
    font-size: ${({ $isTextLong }) => ($isTextLong ? 28 : 36)}px;
    line-height: 1.1;
    background-color: ${({ $hasOnsiteSetup }) => ($hasOnsiteSetup ? 'transparent' : 'palevioletred')};
    font-weight: 800;
    color: black;
    text-align: center;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const SubLabel = styled.span`
    font-size: 18px;
    color: black;
    text-align: center;
`;

const ColumnName = styled.span`
    font-size: 20px;
    font-weight: 600;
    line-height: 1.1;
    color: black;
    text-align: center;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    -webkit-box-orient: vertical;
    margin-top: 2px;
`;

const TagFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 4px;
`;

const QRCodeValue = styled.span`
    font-size: 8px;
    color: #666;
    font-family: monospace;
    text-align: center;
    margin-top: auto;
    padding-top: 2px;
`;

const SideRoundLabel = styled.span<{ $roundColor: string }>`
    font-size: 14px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    color: white;
    background-color: ${({ $roundColor }) => $roundColor};
    opacity: 0.5;
`;
